import React, { useContext, useState } from 'react';
import { Configure } from 'react-instantsearch-dom';
import styled from 'styled-components';

// Components
import Button from '@atoms/Button';
import PropertyListItemListed from '@listItems/PropertyListItemListed';
import ConfirmationModal from '@modals/ConfirmationModal';
import InfoModal from '@modals/InfoModal';
import SearchContainer from '@search/SearchContainer';
import SearchHits from '@search/SearchHits';
import SearchSortBy from '@search/SearchSortBy';
import SearchStats from '@search/SearchStats';
import {
    BodyLarge,
    DivWithShadowAlternate,
    H3,
    LinkSmall,
    LinkText,
    SectionWrapperInner,
    SectionWrapperOuter,
} from '@styled';
import TrackPageView from '@tracking/TrackPageView';
import PropertiesMobileSubnav from './PropertiesMobileSubnav';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import SectionHeader from '../../../_molecules/SectionHeader';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import PagingToolbar from '../../../../../../algolia-search/js/components/sections/PagingToolbar';
import SearchBox from '../../../../../../algolia-search/js/components/elements/SearchBox';

// Functionality
import { LoadingContext } from '@context/LoadingProvider';
import { mediaQueries } from '@general/breakpoints';
import { SAVED_PROPERTIES_QUERY } from '@general/graphql/properties.graph';
import { createPropertyActivatedAt, createPropertyExpiredAt } from '@general/helpers/propertyDates.helpers';
import { getAlgoliaIndexNameProperties } from '@general/helpers/search.helpers';
import { mapSearchPropertyToProps } from '@general/transforms/property.transform';
import useDocumentTitle from '@hooks/useDocumentTitle';
import useReactivateProperties from '@hooks/useReactivateProperties';
import useViewer from '@hooks/useViewer';
import useViewerProperties from '@hooks/useViewerProperties';
import { useNewPropertyModal } from '../../../modals/NewPropertyModal';
import { sortOptionsByType } from '../../../../../../algolia-search/js/data/sortOptions';
import { searchStateToUrl, urlToSearchState } from '../../../../../../algolia-search/js/helpers/search';
import useSortBy from '../../../../../../algolia-search/js/hooks/useSortBy';
import { getUnixTimestamp } from '@general/dates';

const DEBOUNCE_TIME = 400;
const PAGE_SIZE = 10;
const ALGOLIA_REFRESH_DELAY = 3000;
const expirationInterval = getUnixTimestamp() + process.env.PROPERTY_IS_EXPIRING * 3600;

const HeadingWrapper = styled.div`
    margin-bottom: 52px;
`;

const ActionsContainerStyled = styled.div`
    display: flex;
    flex-direction: column;

    button {
        &:not(:first-child) {
            margin-top: 1rem;
        }
    }

    @media ${mediaQueries.lg} {
        display: block;

        button {
            &:not(:first-child) {
                margin-left: 1rem;
                margin-top: 0;
            }
        }
    }
`;

const propertySortOptions = sortOptionsByType.listed_properties.map(option => {
    if (option.indexSuffix === '_activated_at_desc') {
        option.value = `${getAlgoliaIndexNameProperties()}_activated_at_desc`;
    } else if (option.indexSuffix === '_expires_at_desc') {
        option.value = `${getAlgoliaIndexNameProperties()}_expires_at_desc`;
    } else if (option.indexSuffix === '') {
        option.value = getAlgoliaIndexNameProperties();
    } else if (option.indexSuffix === '_title_asc') {
        option.value = `${getAlgoliaIndexNameProperties()}_title_asc`;
    }

    return option;
});

const ListedPropertiesPage = () => {
    //------------------------------
    // State/Hooks
    //------------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const { sortBy: sortByInitial } = useSortBy({ index: 'listed_properties' });
    const [searchState, setSearchState] = useState(urlToSearchState(window.location));
    const [searchRefresh, setSearchRefresh] = useState(false);
    const [stateUpdateTimeoutToken, setStateUpdateTimeoutToken] = useState(null);
    const [sortBy, setSortBy] = useState(
        propertySortOptions.find(option => option.indexSuffix === sortByInitial.indexSuffix) || propertySortOptions[0]
    );

    const [multiSelectedPropertyIds, setMultiSelectedPropertyIds] = useState([]);
    const [propertiesToReactivate, setPropertiesToReactivate] = useState([]);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [confirmationModalBodyText, setConfirmationModalBodyText] = useState('');
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [infoModalBodyText, setInfoModalBodyText] = useState('');
    const [infoModalHeadingText, setInfoModalHeadingText] = useState('');

    const { viewer } = useViewer();
    const { data: viewerProperties } = useViewerProperties();
    const { openModal: openNewPropertyModal } = useNewPropertyModal();
    const [reactivateProperties] = useReactivateProperties({
        propertyIds: propertiesToReactivate,
        onCompleted: response => {
            setIsLoading(false);
            setInfoModalHeadingText('Success');
            setInfoModalBodyText('Your properties have been reactivated.');
            setIsInfoModalOpen(true);
            setMultiSelectedPropertyIds([]);
            setPropertiesToReactivate([]);
            setTimeout(() => {
                setSearchRefresh(true);
                setTimeout(() => {
                    setSearchRefresh(false);
                });
            }, ALGOLIA_REFRESH_DELAY);
        },
        onError: () => {
            setIsLoading(false);
            setInfoModalHeadingText('Error');
            setInfoModalBodyText(
                'There was an error while reactivating your properties. Please refresh the page and try again.'
            );
            setIsInfoModalOpen(true);
        },
    });

    useDocumentTitle('My Listed Properties');

    //------------------------------
    // Helpers
    //------------------------------

    const updateUrl = ({ newSearchState, newSortBy }) => {
        clearTimeout(stateUpdateTimeoutToken);
        setStateUpdateTimeoutToken(
            setTimeout(() => {
                const url = searchStateToUrl({
                    searchType: 'properties',
                    sortBy: newSortBy.queryParam,
                    searchState: {
                        query: newSearchState.query,
                    },
                });

                window.history.pushState('', '', url);

                if (window.ga) {
                    window.ga('send', 'pageView', url);
                }
            }, DEBOUNCE_TIME)
        );
    };

    const refreshSearchContainer = () => {
        setSearchRefresh(true);
        setTimeout(() => {
            setSearchRefresh(false);
        }, ALGOLIA_REFRESH_DELAY);
    };

    const reactivateDesignatedProperties = () => {
        setIsLoading(true);

        const variables = {
            activatedAt: createPropertyActivatedAt(),
            expiresAt: createPropertyExpiredAt(),
        };

        propertiesToReactivate.forEach((id, index) => {
            variables[`id${index}`] = id;
        });

        reactivateProperties({
            variables,
        });
    };

    //------------------------------
    // Render
    //------------------------------

    if (!viewer || !viewerProperties) {
        return null;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                <PropertiesMobileSubnav />

                <SectionWrapperOuter>
                    <SectionWrapperInner>
                        <SectionHeader title="My ICSC Properties" />

                        <div className="flex justify-content-between mb-1.5rem">
                            <SectionBreadcrumbs className="mt-2">
                                <SectionBreadcrumb route="/my-icsc/properties">Properties</SectionBreadcrumb>
                                <SectionBreadcrumb isActive>My Listed Properties</SectionBreadcrumb>
                            </SectionBreadcrumbs>
                        </div>

                        <HeadingWrapper>
                            <H3 className="mb-1rem">My Listed Properties</H3>
                            <BodyLarge>
                                Manage your properties. List, enhance and remove out of date properties.
                            </BodyLarge>
                        </HeadingWrapper>

                        <DivWithShadowAlternate className="p-4">
                            <p className="font-weight-bold">Need to create a new property listing?</p>
                            <ActionsContainerStyled>
                                <Button
                                    onClick={() =>
                                        openNewPropertyModal(() => {
                                            refreshSearchContainer();
                                        })
                                    }
                                >
                                    Add a New Listing
                                </Button>

                                <Button
                                    disabled={multiSelectedPropertyIds.length === 0}
                                    onClick={() => {
                                        setConfirmationModalBodyText(
                                            `Reactivate ${multiSelectedPropertyIds.length} ${
                                                multiSelectedPropertyIds.length === 1 ? 'property' : 'properties'
                                            }?`
                                        );

                                        setPropertiesToReactivate(multiSelectedPropertyIds);
                                        setIsConfirmationModalOpen(true);
                                    }}
                                >
                                    Reactivate Selected
                                </Button>

                                <Button
                                    onClick={() => {
                                        setConfirmationModalBodyText('Reactivate all properties?');
                                        setPropertiesToReactivate(viewerProperties.map(p => p.id));
                                        setIsConfirmationModalOpen(true);
                                    }}
                                >
                                    Reactivate All
                                </Button>
                            </ActionsContainerStyled>

                            <div className="mt-2rem">
                                <SearchContainer
                                    index="properties"
                                    refresh={searchRefresh}
                                    searchState={searchState}
                                    onSearchStateChange={newSearchState => {
                                        updateUrl({ newSearchState, newSortBy: sortBy });
                                        setSearchState(newSearchState);
                                    }}
                                >
                                    <Configure
                                        clickAnalytics
                                        filters={
                                            sortBy.indexSuffix === '_expires_at_desc'
                                                ? `manager.recordId:${
                                                      viewer.recordId
                                                  } AND expiresAtTimestamp <= ${expirationInterval}`
                                                : ` manager.recordId:${viewer.recordId}`
                                        }
                                        hitsPerPage={PAGE_SIZE}
                                    />

                                    <div className="mb-1rem">
                                        <SearchBox debounceTime={DEBOUNCE_TIME} placeholder="Search for..." />
                                    </div>

                                    <div className="listing-filters d-flex justify-between mb-1rem">
                                        <div>
                                            <SearchStats pageSize={PAGE_SIZE} />
                                        </div>

                                        <div>
                                            <SearchSortBy
                                                defaultRefinement={sortBy.value}
                                                items={propertySortOptions}
                                                onChange={newSort => {
                                                    setSortBy(newSort);
                                                    updateUrl({
                                                        newSearchState: searchState,
                                                        newSortBy: newSort,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <SearchHits
                                        HitComponent={PropertyListItemListed}
                                        favoritesProp="properties"
                                        favoritesQuery={SAVED_PROPERTIES_QUERY}
                                        index="properties"
                                        resultsPerRow={1}
                                        searchType="properties"
                                        shouldLogConversions={false}
                                        getIsSelectedFunc={property =>
                                            multiSelectedPropertyIds.includes(property.objectID)
                                        }
                                        transformFunc={mapSearchPropertyToProps}
                                        onDelete={() => {
                                            setTimeout(() => {
                                                setSearchRefresh(true);
                                                setTimeout(() => {
                                                    setSearchRefresh(false);
                                                });
                                            }, ALGOLIA_REFRESH_DELAY);
                                        }}
                                        onToggleSelected={property => {
                                            const foundIndex = multiSelectedPropertyIds.findIndex(
                                                id => id === property.objectID
                                            );

                                            if (foundIndex >= 0) {
                                                multiSelectedPropertyIds.splice(foundIndex, 1);
                                                setMultiSelectedPropertyIds([...multiSelectedPropertyIds]);
                                            } else {
                                                setMultiSelectedPropertyIds([
                                                    ...multiSelectedPropertyIds,
                                                    property.objectID,
                                                ]);
                                            }
                                        }}
                                    />

                                    <div className="mt-1rem">
                                        <PagingToolbar scrollToRef={null} />
                                    </div>
                                </SearchContainer>
                            </div>
                        </DivWithShadowAlternate>

                        <LinkText className="block mt-3">
                            For assistance uploading 50 properties or more, please send an email to{' '}
                            <LinkSmall href="mailto:properties@icsc.com">properties@icsc.com</LinkSmall>.
                        </LinkText>
                    </SectionWrapperInner>
                </SectionWrapperOuter>

                <ConfirmationModal
                    bodyText={confirmationModalBodyText}
                    cancelButtonText="Cancel"
                    confirmButtonText="Reactivate"
                    headingText="Reactivate Properties"
                    isOpen={isConfirmationModalOpen}
                    onClose={() => setIsConfirmationModalOpen(false)}
                    onConfirm={() => {
                        setIsConfirmationModalOpen(false);
                        reactivateDesignatedProperties();
                        refreshSearchContainer();
                    }}
                />

                <InfoModal
                    bodyText={infoModalBodyText}
                    headingText={infoModalHeadingText}
                    isOpen={isInfoModalOpen}
                    onClose={() => setIsInfoModalOpen(false)}
                />
            </TrackPageView>
        </ErrorHandler>
    );
};

export default ListedPropertiesPage;
